import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  setSignupData,
  setError,
  setVerificationStep,
} from "../../store/slices/signupSlice";
import {
  setCredentials,
  updateUserTypeSelected,
} from "../../store/slices/authSlice";
import { authService } from "../../services/api";
import ImageUploadModal from "../profile/ImageUploadModal";
import {
  LICENSE_FILE_ACCEPT,
  pickValidLicenseFile,
} from "../../utils/licenseFile";

const AgencyDetailsStep = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { error } = useSelector((state) => state.signup);
  const auth = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [showImageUploadModal, setShowImageUploadModal] = useState(false);

  const [formData, setFormData] = useState({
    agencyName: "",
    adminName: "",
    hasLicense: false,
    agencyLicenseNumber: "",
    licenseFile: null,
  });

  const handleBack = () => {
    dispatch(setVerificationStep("type"));
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Mirrors AgentDetailsStep: unchecking clears both licence values so an
  // unchecked box always submits an empty licence, never a stale one.
  const handleLicenseCheckboxChange = (checked) => {
    setFormData((prev) => ({
      ...prev,
      hasLicense: checked,
      agencyLicenseNumber: checked ? prev.agencyLicenseNumber : "",
      licenseFile: checked ? prev.licenseFile : null,
    }));
  };

  const handleLicenseFileChange = (event) => {
    // Shared 10MB + MIME check (src/utils/licenseFile.js).
    const file = pickValidLicenseFile(event);
    if (!file) return;

    setFormData((prev) => ({
      ...prev,
      licenseFile: file,
    }));
  };

  const handleRemoveLicenseFile = () => {
    setFormData((prev) => ({
      ...prev,
      licenseFile: null,
    }));
    // Reset the file input
    const fileInput = document.getElementById("agency-license-file-upload");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleLogoUpload = (imageUrl) => {
    setLogoPreview(imageUrl);
    setShowImageUploadModal(false);

    // Convert the blob URL to a File object for upload
    fetch(imageUrl)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], "agency-logo.jpg", {
          type: "image/jpeg",
        });
        setLogoFile(file);
      });
  };

  const handleCreateAgentAccount = () => {
    dispatch(setSignupData({ accountType: "agent" }));
    dispatch(setVerificationStep("details"));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.agencyName || !formData.adminName) {
      dispatch(setError("Please fill in all required fields"));
      return;
    }

    // Same rule as AgentDetailsStep: claiming a licence means number AND
    // document. Leaving the box unchecked stays a valid, submittable state.
    if (formData.hasLicense && !formData.agencyLicenseNumber) {
      dispatch(setError("Please enter your license number"));
      return;
    }

    if (formData.hasLicense && !formData.licenseFile) {
      dispatch(setError("Please upload your license document"));
      return;
    }

    setLoading(true);
    dispatch(setError(null));

    const agencyData = {
      agency_name: String(formData.agencyName).trim(),
      admin_name: String(formData.adminName).trim(),
      // Sent unconditionally (empty string when unchecked) — unchanged
      // behaviour the backend already tolerates.
      agent_license_number: String(formData.agencyLicenseNumber).trim(),
      logo: logoFile, // This will be mapped to 'profile_picture' in the API call
      // Sent as 'license_document' (NOT 'document' — that name is swallowed
      // as the agency logo by the create-agency endpoint).
      licenseFile: formData.hasLicense ? formData.licenseFile : null,
    };

    console.log("Submitting agency data:", agencyData);

    try {
      const response = await authService.createAgency(agencyData);

      if (response.success) {
        console.log("Agency creation successful, updating auth state");

        // Store agency details without the File object for Redux
        const agencyDetailsForRedux = {
          agency_name: agencyData?.agency_name,
          admin_name: agencyData?.admin_name,
          agent_license_number: agencyData?.agent_license_number,
          logo: null, // Don't store File object in Redux
        };

        dispatch(setSignupData({ agencyDetails: agencyDetailsForRedux }));
        dispatch(setVerificationStep("complete"));
      } else {
        dispatch(setError(response.error || "Failed to create agency"));
      }
    } catch (error) {
      console.error("Submit error:", error);
      dispatch(setError("An unexpected error occurred"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex items-center mb-6">
        <button
          onClick={handleBack}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-5 h-5 mr-2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.75 19.5L8.25 12l7.5-7.5"
            />
          </svg>
          Back
        </button>
      </div>

      <div className="w-full">
        <div className="flex items-center justify-center w-12 h-12 bg-[#374DF5] rounded-2xl mb-4">
          <svg
            width="20"
            height="16"
            viewBox="0 0 20 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13.7917 15.125V13.5417C13.7917 12.7018 13.458 11.8964 12.8642 11.3025C12.2703 10.7086 11.4649 10.375 10.625 10.375H4.29167C3.45181 10.375 2.64636 10.7086 2.0525 11.3025C1.45863 11.8964 1.125 12.7018 1.125 13.5417V15.125M18.5417 15.125V13.5417C18.5411 12.84 18.3076 12.1584 17.8777 11.6039C17.4479 11.0494 16.846 10.6533 16.1667 10.4779M13 0.977917C13.6812 1.15232 14.2849 1.54847 14.716 2.10391C15.1472 2.65935 15.3812 3.34249 15.3812 4.04563C15.3812 4.74876 15.1472 5.4319 14.716 5.98734C14.2849 6.54278 13.6812 6.93893 13 7.11333M10.625 4.04167C10.625 5.79057 9.20724 7.20833 7.45833 7.20833C5.70943 7.20833 4.29167 5.79057 4.29167 4.04167C4.29167 2.29277 5.70943 0.875 7.45833 0.875C9.20724 0.875 10.625 2.29277 10.625 4.04167Z"
              stroke="white"
              stroke-width="1.16667"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>

        <h2 className="text-2xl leading-[28px] font-bold text-gray-900 mb-2">
          Setup Agency
        </h2>
        <p className="text-[14px] leading-[20px] text-gray-600 mb-8">
          Don't have an agency?
          <button
            onClick={handleCreateAgentAccount}
            className="text-[#2283FF] hover:text-[#1a6cd4] ml-1 font-medium"
          >
            Create Agent account
          </button>
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-base font-medium text-[#000000] mb-2">
              Agency name
            </label>
            <div className="relative">
              {logoPreview && (
                <div className="absolute left-4 top-[23px] -translate-y-1/2">
                  <img
                    src={logoPreview}
                    alt="Agency logo"
                    className="w-8 h-8 rounded-full object-cover"
                  />
                </div>
              )}
              <input
                type="text"
                className={`w-full ${
                  logoPreview ? "pl-16" : "pl-4"
                } pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2283FF] focus:border-[#2283FF]`}
                placeholder="Enter agency name"
                value={formData.agencyName}
                onChange={(e) =>
                  handleInputChange("agencyName", e.target.value)
                }
                required
              />

              <span className="text-xs text-gray-600">
                Please Enter Your Complete Agency Name as Per The Government
                Records
              </span>
            </div>

            <div className="mt-4 space-y-2">
              <label className="block text-base font-medium text-[#000000]">
                Agency logo
              </label>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowImageUploadModal(true)}
                  className="px-4 py-2 rounded-lg bg-[#2283FF] hover:bg-[#1a6cd4] text-white font-medium transition-colors"
                >
                  {logoPreview ? "Change logo" : "Upload agency logo"}
                </button>

                {logoPreview && (
                  <button
                    type="button"
                    onClick={() => {
                      setLogoPreview(null);
                      setLogoFile(null);
                    }}
                    className="px-4 py-2 rounded-lg border border-[#2283FF] text-[#2283FF] hover:text-[#1a6cd4] hover:border-[#1a6cd4] font-medium transition-colors"
                  >
                    Remove image
                  </button>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-base font-medium text-[#000000] mb-2">
              Admin name
            </label>
            <input
              type="text"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2283FF] focus:border-[#2283FF]"
              placeholder="Ex: John Doe"
              value={formData.adminName}
              onChange={(e) => handleInputChange("adminName", e.target.value)}
              required
            />
          </div>

          {/* License Checkbox */}
          <div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="agencyHasLicense"
                checked={formData.hasLicense}
                onChange={(e) => handleLicenseCheckboxChange(e.target.checked)}
                className="h-4 w-4 text-[#2283FF] focus:ring-[#2283FF] border-gray-300 rounded"
              />
              <label
                htmlFor="agencyHasLicense"
                className="ml-2 block text-sm font-medium text-black"
              >
                I have a license
              </label>
            </div>
          </div>

          {/* Conditional License Fields */}
          {formData.hasLicense && (
            <>
              <div>
                <label className="block text-base font-medium text-[#000000] mb-2">
                  License number*
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2283FF] focus:border-[#2283FF]"
                  placeholder="202401-1234"
                  value={formData.agencyLicenseNumber}
                  onChange={(e) =>
                    handleInputChange("agencyLicenseNumber", e.target.value)
                  }
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-black mb-1">
                  Upload License Document*
                </label>
                {!formData.licenseFile ? (
                  <div className="flex items-center gap-3">
                    <input
                      type="file"
                      id="agency-license-file-upload"
                      onChange={handleLicenseFileChange}
                      className="hidden"
                      accept={LICENSE_FILE_ACCEPT}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        document
                          .getElementById("agency-license-file-upload")
                          ?.click()
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          document
                            .getElementById("agency-license-file-upload")
                            ?.click();
                        }
                      }}
                      className="inline-flex items-center mt-1 px-3 py-2 text-sm font-medium text-white bg-[#2283FF] rounded-md hover:bg-[#1a6cd4] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2283FF]"
                      aria-label="Upload license document"
                      tabIndex="0"
                    >
                      Upload license
                    </button>
                  </div>
                ) : (
                  <div className="border border-gray-300 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <svg
                            className="w-5 h-5 text-green-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {formData.licenseFile.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {(formData.licenseFile.size / 1024 / 1024).toFixed(
                              2
                            )}{" "}
                            MB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleRemoveLicenseFile}
                        className="text-red-500 hover:text-red-700"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          <button
            type="submit"
            disabled={
              loading ||
              !formData.agencyName ||
              !formData.adminName ||
              (formData.hasLicense &&
                (!formData.agencyLicenseNumber || !formData.licenseFile))
            }
            className={`w-full py-3 px-4 rounded-lg transition-colors font-medium ${
              loading ||
              !formData.agencyName ||
              !formData.adminName ||
              (formData.hasLicense &&
                (!formData.agencyLicenseNumber || !formData.licenseFile))
                ? "bg-[#2283FF1A] text-[#2283FF] cursor-not-allowed"
                : "bg-[#2283FF] hover:bg-[#1a6cd4] text-white"
            }`}
          >
            {loading ? "Creating agency..." : "Create agency"}
          </button>
        </form>
      </div>

      {/* Image Upload Modal */}
      {showImageUploadModal && (
        <ImageUploadModal
          onClose={() => setShowImageUploadModal(false)}
          onUpload={handleLogoUpload}
          documentType="profile_photo"
        />
      )}
    </>
  );
};

export default AgencyDetailsStep;
